/**
 * «Проверьте сами»: доход от фактов торговли робота и ползунков посетителя.
 * Одна формула для витрины на главной и калькулятора на странице торговли:
 *
 *   фьючерсы:  контрактов в день × чистыми на контракт × лотов = робот в день
 *   крипта:    сделок в день × чистыми на сделку (на лот)       = робот в день
 *
 * По умолчанию ползунки стоят на фактах (backend shared/history_facts.py).
 * Клиенту — 50% прибыли (PLATFORM_SHARE), убыточный день делится как есть.
 */
import { CryptoCalculatorDataModel } from 'api/http/models'
import { PLATFORM_SHARE, tradesFactorFor } from './calc'

export const FUTURES_DAYS_YEAR = 250 // торговых дней CME
export const CRYPTO_DAYS_YEAR = 365

export type HistoryFacts = NonNullable<CryptoCalculatorDataModel['history']>

/** Тейк, с которым набрана история; без него — тейк инструмента. */
export const historyTake = (h: HistoryFacts, fallback: number) =>
    h.takeProfit || fallback

/** Чистыми на 1 контракт по факту (после комиссии биржи, до 50/50). */
export const factPerContract = (h: HistoryFacts) =>
    h.volumePerLotDay ? h.netPerLotDay / h.volumePerLotDay : 0

/**
 * Чистыми на контракт при тейке take: факт плюс разница тейков в деньгах.
 * Что робот берёт сверх тейка (выходы после доливок), остаётся как в факте.
 */
export const perContractAt = (
    h: HistoryFacts,
    take: number,
    histTake: number,
    pointValue: number
) => factPerContract(h) + (take - histTake) * pointValue

/** Контрактов в день на 1 лот при тейке take: факт, с другим тейком — меньше/больше. */
export const contractsAt = (h: HistoryFacts, take: number, histTake: number) =>
    Math.abs(take - histTake) < 1e-9
        ? h.volumePerLotDay
        : h.volumePerLotDay * tradesFactorFor(take, histTake)

/** Чистыми на сделку на 1 монету лота (крипта). */
export const factPerTrade = (h: HistoryFacts) =>
    h.tradesPerDay ? h.netPerLotDay / h.tradesPerDay : 0

export interface Income {
    robotDay: number
    clientDay: number
    clientMonth: number
    clientYear: number
    yearPct: number | null
}

export const income = (
    robotDay: number,
    deposit: number | undefined,
    daysYear: number
): Income => {
    const clientDay = robotDay > 0 ? robotDay * (1 - PLATFORM_SHARE) : robotDay
    const clientYear = clientDay * daysYear
    return {
        robotDay,
        clientDay,
        clientMonth: clientYear / 12,
        clientYear,
        yearPct: deposit ? (clientYear / deposit) * 100 : null,
    }
}

/** Шаг ползунка: «круглое» число около max / 100. */
export const niceStep = (max: number) => {
    const raw = max / 100
    const p = 10 ** Math.floor(Math.log10(raw || 1))
    const k = raw / p
    return (k < 2 ? 1 : k < 5 ? 2 : 5) * p
}

/**
 * Инструмент без сделок: «факты» для расчёта строятся по образцу (инструмент
 * с историей).  Число сделок/контрактов в день — как у образца; доход на
 * сделку — по тейку и комиссии выбранного инструмента (вход и выход по тейку).
 */
export const futuresHistoryFromReference = (
    ref: HistoryFacts,
    take: number,
    pointValue: number,
    feePerLot: number
): HistoryFacts => {
    const perContract = take * pointValue - feePerLot
    return {
        ...ref,
        takeProfit: take,
        netPerLotDay: ref.volumePerLotDay * perContract,
        totalProfit: 0,
        totalVolume: 0,
        daily: [],
    }
}

export const cryptoHistoryFromReference = (
    ref: HistoryFacts,
    take: number,
    price: number,
    takerFee: number,
    makerFee: number
): HistoryFacts => {
    // На 1 монету лота: тейк минус комиссия входа (taker) и выхода (maker).
    const perTrade = take - (price * (takerFee + makerFee)) / 100
    return {
        ...ref,
        takeProfit: take,
        netPerLotDay: ref.tradesPerDay * perTrade,
        volumePerLotDay: ref.tradesPerDay * 2,
        totalProfit: 0,
        totalVolume: 0,
        daily: [],
    }
}
